import type { FrameDto } from '@codeframe/api-contract';
import { ArrowUpCircle, Cpu, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useActiveFrame, useFrameCatalog, useInstall, useInstallHardware, useModules, useParts, useUninstall } from '../api/hooks.js';
import { MechSchematic } from '../components/MechSchematic.js';
import { Loading, Page, PageHeader, errorMessage } from '../components/Page.js';
import { Alert, Badge, Button, Meter, Panel, Stat, selectClass } from '../components/ui.js';
import s from './FramePage.module.css';

export function FramePage() {
  const frame = useActiveFrame();
  const modules = useModules();
  const catalog = useFrameCatalog();
  const install = useInstall();
  const uninstall = useUninstall();
  const [picker, setPicker] = useState<Record<number, string>>({});
  if (!frame) return <Loading />;
  const st = frame.stats;
  const error = install.error ?? uninstall.error;

  return (
    <Page>
      <PageHeader eyebrow={`Frame bay · ${frame.archetype}`} title={frame.name} description={frame.description} actions={<Badge tone="accent">Frame LV {frame.level} · {frame.xp} XP</Badge>} />
      <div className={s.grid}>
        <div className={s.col}>
          <Panel label="Schematic" flush cut>
            <MechSchematic slots={frame.slots} name={frame.name} art={frame.art} />
          </Panel>
          <HardwarePanel frame={frame} />
        </div>
        <div className={s.col}>
          <Panel label="Core systems">
            <div className={s.stats}>
              <Stat label="Health" value={st.maxHealth} />
              <Stat label="Energy" value={st.maxEnergy} unit={`+${st.energyRegen}/tick`} />
              <Stat label="Armour" value={st.armor} />
              <Stat label="Processor" value={st.cyclesPerTick} unit="cyc/tick" />
              <Stat label="Memory" value={st.memory} unit="units" />
              <Stat label="Threads" value={st.threads} />
            </div>
          </Panel>
          <Panel
            label="Software"
            title="Routine threads"
            actions={
              <div style={{ width: 200 }}>
                <Meter value={frame.cpuAllocated} max={st.cyclesPerTick} display={`${frame.cpuAllocated}/${st.cyclesPerTick} cyc`} color={frame.cpuAllocated > st.cyclesPerTick ? 'var(--err)' : undefined} />
              </div>
            }
            flush
          >
            {error && (
              <div style={{ padding: 12 }}>
                <Alert>{errorMessage(error)}</Alert>
              </div>
            )}
            {frame.software.map((slot) => {
              const choice = picker[slot.slotIndex] ?? '';
              return (
                <div key={slot.slotIndex} className={s.slot}>
                  <div>
                    <span className="label">
                      <Cpu size={11} /> Thread {slot.slotIndex}
                    </span>
                    {slot.module ? (
                      <div>
                        <span className={s.slotName}>{slot.module.name}</span>{' '}
                        <Badge>v{slot.module.version}</Badge> <Badge tone="accent">{slot.module.cycles} cyc</Badge> <Badge tone="ok">{slot.module.grade}</Badge>
                        {slot.module.latestVersion > slot.module.version && <Badge tone="warn">v{slot.module.latestVersion} available</Badge>}
                      </div>
                    ) : (
                      <div className="muted">Empty thread</div>
                    )}
                  </div>
                  <div className={s.actions}>
                    {slot.module && slot.module.latestVersion > slot.module.version && (
                      <Button
                        size="small"
                        icon={<ArrowUpCircle size={14} />}
                        onClick={() => install.mutate({ frameId: frame.id, moduleId: slot.module!.id, slotIndex: slot.slotIndex })}
                      >
                        Update
                      </Button>
                    )}
                    <select className={selectClass} style={{ height: 28, maxWidth: 170 }} value={choice} onChange={(e) => setPicker({ ...picker, [slot.slotIndex]: e.target.value })} aria-label={`Module for thread ${slot.slotIndex}`}>
                      <option value="">Choose module…</option>
                      {modules.data?.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} ({m.current.resources.estimatedMaxCycles ?? m.current.resources.measuredMaxCycles} cyc)
                        </option>
                      ))}
                    </select>
                    <Button size="small" variant="primary" disabled={!choice} loading={install.isPending && install.variables?.slotIndex === slot.slotIndex} onClick={() => install.mutate({ frameId: frame.id, moduleId: choice, slotIndex: slot.slotIndex })}>
                      Install
                    </Button>
                    {slot.module && (
                      <Button size="small" variant="ghost" icon={<Trash2 size={14} />} aria-label="Uninstall" onClick={() => uninstall.mutate({ frameId: frame.id, slotIndex: slot.slotIndex })} />
                    )}
                  </div>
                </div>
              );
            })}
          </Panel>
          <Panel label="Hangar" title="Frame catalogue">
            <div className={s.catalog}>
              {catalog.data?.map((f) => (
                <div key={f.id} className={s.frameCard}>
                  <div className={s.frameHead}>
                    <b>{f.name}</b>
                    <Badge tone={f.owned ? 'ok' : 'neutral'}>{f.owned ? 'Owned' : f.archetype}</Badge>
                  </div>
                  <span className="muted">{f.tagline}</span>
                  {!f.owned && <span className="label" style={{ textTransform: 'none', letterSpacing: 0 }}>{f.acquisition}</span>}
                </div>
              ))}
            </div>
          </Panel>
          <Link to="/workshop">
            <Button variant="primary">Write new software in the Workshop</Button>
          </Link>
        </div>
      </div>
    </Page>
  );
}

/** Hardware slots: swap in any owned part that fits; the server checks software still runs. */
function HardwarePanel({ frame }: { frame: FrameDto }) {
  const parts = useParts();
  const installHardware = useInstallHardware();
  const owned = parts.data ?? [];
  return (
    <Panel label="Hardware" title="Installed parts">
      {installHardware.error && (
        <div className={s.hardwareError}>
          <Alert>{errorMessage(installHardware.error)}</Alert>
        </div>
      )}
      <div className={s.parts}>
        {frame.slots.map((slot) => {
          // Parts that fit this slot and aren't installed somewhere else.
          const options = owned.filter(
            (p) =>
              slot.accepts.includes(p.category) &&
              (!p.installed ||
                (p.installed.frameId === frame.id && p.installed.slot === slot.slot)),
          );
          const upgrade = options.some((p) => !p.installed && p.tier > (slot.part?.tier ?? 0));
          const busy = installHardware.isPending && installHardware.variables?.slot === slot.slot;
          return (
            <div key={slot.slot} className={s.part}>
              <span className="label">{slot.slot.replace('.', ' · ')}</span>
              <div className={s.partBody}>
                <div className={s.partPick}>
                  <select
                    className={selectClass}
                    value={slot.part?.instanceId ?? ''}
                    disabled={busy || !parts.data}
                    onChange={(e) =>
                      installHardware.mutate({
                        frameId: frame.id,
                        slot: slot.slot,
                        partInstanceId: e.target.value || null,
                      })
                    }
                    aria-label={`Part for ${slot.slot}`}
                  >
                    <option value="">Empty</option>
                    {options.map((p) => (
                      <option key={p.instanceId} value={p.instanceId}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  {upgrade && <Badge tone="ok">Upgrade ready</Badge>}
                </div>
                <span className="muted">
                  {slot.part ? slot.part.description : `Accepts ${slot.accepts.join(', ')}.`}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
